import { useState } from "react"
import { AxiosError } from "axios"
import { Mail, Loader2 } from "lucide-react"
import { toast } from "sonner"

import { useSendMailCorrespondence } from "@apis/correspondences/useCorrespondences"
import { ConfirmDialog } from "@components/ConfirmDialog/ConfirmDialog"
import { Button } from "@components/ui/button"
import { useCorrespondenceResolver } from "@components/PlanCorrespondencesTable/utils/useCorrespondenceResolver"

interface TButtonProps {
  account_correspondence_id: number
  account_id: string
  isWelcome: boolean
  correspondence_name: string
}

export const SendMailButton = ({
  account_correspondence_id,
  account_id,
  isWelcome,
  correspondence_name,
}: TButtonProps) => {
  const [isConfirmOpen, setIsConfirmOpen] = useState(false)
  const { getCorrespondenceId, isCorrespondenceResolverFetching } =
    useCorrespondenceResolver({
      account_id,
      account_correspondence_id,
      isWelcome,
    })
  const sendMailCorrespondenceMutation = useSendMailCorrespondence()

  const isSending =
    sendMailCorrespondenceMutation.isPending || isCorrespondenceResolverFetching

  const handleConfirm = async () => {
    const correspondenceId = await getCorrespondenceId()
    if (!correspondenceId) {
      setIsConfirmOpen(false)
      toast.error("Error fetching welcome letter!")
      return
    }

    sendMailCorrespondenceMutation.mutate(
      {
        account_correspondence_id: correspondenceId,
      },
      {
        onSuccess: () => {
          toast.success("Mail successfully Sent!")
        },
        onError: (error: string | AxiosError) =>
          toast.error(`Error sending mail! ${error}`),
        onSettled: () => setIsConfirmOpen(false),
      },
    )
  }

  return (
    <>
      <Button
        size="icon"
        variant="ghost"
        onClick={() => setIsConfirmOpen(true)}
        disabled={(!account_correspondence_id && !isWelcome) || isSending}
      >
        {sendMailCorrespondenceMutation.isPending ? (
          <Loader2 className="tw:animate-spin" />
        ) : (
          <Mail />
        )}
      </Button>
      <ConfirmDialog
        open={isConfirmOpen}
        onOpenChange={setIsConfirmOpen}
        title="Resend this correspondence by mail?"
        description={`This will mail a physical copy of ${correspondence_name} to the customer.`}
        confirmLabel="Send mail"
        isPending={isSending}
        onConfirm={handleConfirm}
      />
    </>
  )
}
